import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, addDays } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { 
  Bell, 
  CheckCircle2, 
  Circle, 
  Calendar as CalendarIcon,
  MapPin,
  Gift,
  Heart,
  CreditCard,
  Phone,
  Loader2,
  X,
  ChevronDown,
  Clock
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useCalendar, type CalendarEvent } from '@/hooks/use-calendar';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const eventConfig = {
  birthday: { 
    icon: Gift, 
    color: 'bg-pink-500/20 text-pink-600 border-pink-300',
    label: 'Doğum Günü'
  },
  anniversary: { 
    icon: Heart, 
    color: 'bg-red-500/20 text-red-600 border-red-300',
    label: 'Yıldönümü'
  },
  payment: { 
    icon: CreditCard, 
    color: 'bg-green-500/20 text-green-600 border-green-300',
    label: 'Ödeme'
  },
  'follow-up': { 
    icon: Phone, 
    color: 'bg-blue-500/20 text-blue-600 border-blue-300',
    label: 'Takip'
  },
  appointment: { 
    icon: CalendarIcon, 
    color: 'bg-purple-500/20 text-purple-600 border-purple-300',
    label: 'Randevu'
  },
  meeting: { 
    icon: Phone, 
    color: 'bg-orange-500/20 text-orange-600 border-orange-300',
    label: 'Toplantı'
  },
  task: { 
    icon: CalendarIcon, 
    color: 'bg-gray-500/20 text-gray-600 border-gray-300',
    label: 'Görev'
  },
  visit_reminder: { 
    icon: MapPin, 
    color: 'bg-amber-500/20 text-amber-600 border-amber-300',
    label: 'Ziyaret Hatırlatıcısı'
  }
};

const completableEventTypes = ['visit_reminder', 'follow-up', 'payment', 'task', 'appointment', 'meeting'];

const isCompletableEvent = (event: CalendarEvent) => {
  return completableEventTypes.includes(event.event_type);
};

const isEventCompleted = (event: CalendarEvent) => {
  return event.status === 'completed' || event.completion_percentage === 100;
};

const isSatisfactionCall = (event: CalendarEvent) => {
  // Check if it's a satisfaction call reminder
  const metadata = event.metadata as any;
  return (
    event.event_type === 'follow-up' &&
    (metadata?.purpose === 'customer_satisfaction' || 
     event.title === 'Memnuniyet Araması' ||
     event.title?.toLowerCase().includes('memnuniyet'))
  );
};

const formatCustomerName = (customer: any) => {
  if (!customer) return null;
  return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'İsimsiz Müşteri';
};

export const TodayReminders = () => {
  const { companyId } = useAuth();
  const { updateEventData, isUpdating } = useCalendar();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [delayDate, setDelayDate] = useState<Date | undefined>(undefined);
  const [delayEventId, setDelayEventId] = useState<string | null>(null);

  // Get today's date in YYYY-MM-DD format
  const today = format(new Date(), 'yyyy-MM-dd');

  // Fetch today's reminders
  const { data: reminders = [], isLoading, refetch } = useQuery({
    queryKey: ['today-reminders', companyId, today],
    queryFn: async () => {
      if (!companyId) return [];

      const primaryQuery = await supabase
        .from('calendar_events')
        .select(`
          *,
          customer:customers(id, first_name, last_name, email, phone),
          visit:visits(id, started_at:entered_at, customer_id, customer:customers(id, first_name, last_name, phone, email))
        `)
        .eq('company_id', companyId)
        .eq('start_date', today)
        .in('status', ['scheduled', 'in_progress'])
        .is('deleted_at', null)
        .order('start_time', { ascending: true })
        .order('priority', { ascending: false });

      if (!primaryQuery.error) {
        return (primaryQuery.data || []) as CalendarEvent[];
      }

      // Some environments do not expose the visits relation or reject enum ordering.
      // Fall back to a simpler query so dashboard reminders still load.
      const fallbackQuery = await supabase
        .from('calendar_events')
        .select(`
          *,
          customer:customers(id, first_name, last_name, email, phone)
        `)
        .eq('company_id', companyId)
        .eq('start_date', today)
        .in('status', ['scheduled', 'in_progress'])
        .is('deleted_at', null)
        .order('start_time', { ascending: true });

      if (fallbackQuery.error) throw fallbackQuery.error;
      return (fallbackQuery.data || []) as CalendarEvent[];
    },
    enabled: !!companyId,
    refetchInterval: 60000, // Refetch every minute
  });

  const toggleEventCompletion = async (event: CalendarEvent) => {
    const isCompleted = isEventCompleted(event);
    const newStatus = isCompleted ? 'scheduled' : 'completed';
    const newCompletionPercentage = isCompleted ? 0 : 100;
    
    try {
      updateEventData({
        eventId: event.id,
        updates: {
          status: newStatus,
          completion_percentage: newCompletionPercentage,
          updated_at: new Date().toISOString()
        }
      });
      
      toast({
        title: isCompleted ? "Hatırlatıcı Tekrar Açıldı" : "Hatırlatıcı Tamamlandı",
        description: `${event.title} ${isCompleted ? 'tekrar açıldı' : 'tamamlandı olarak işaretlendi'}`,
      });
      
      // Refetch to update the list
      setTimeout(() => refetch(), 500);
    } catch (error) {
      console.error('Error toggling event completion:', error);
      toast({
        title: "Hata",
        description: "Hatırlatıcı durumu güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    }
  };

  const dismissReminder = async (event: CalendarEvent) => {
    try {
      updateEventData({
        eventId: event.id,
        updates: {
          status: 'cancelled',
          updated_at: new Date().toISOString()
        }
      });
      
      toast({
        title: "Hatırlatıcı Kapatıldı",
        description: `${event.title} kapatıldı`,
      });
      
      setTimeout(() => refetch(), 500);
    } catch (error) {
      console.error('Error dismissing reminder:', error);
      toast({
        title: "Hata",
        description: "Hatırlatıcı kapatılırken bir hata oluştu.",
        variant: "destructive",
      });
    }
  };

  const delayReminder = async (eventId: string, newDate: Date) => {
    try {
      updateEventData({
        eventId: eventId,
        updates: {
          start_date: format(newDate, 'yyyy-MM-dd'),
          updated_at: new Date().toISOString()
        }
      });
      
      toast({
        title: "Hatırlatıcı Ertelendi",
        description: `Hatırlatıcı ${format(newDate, 'dd.MM.yyyy')} tarihine ertelendi`,
      });
      
      setDelayEventId(null);
      setDelayDate(undefined);
      setTimeout(() => refetch(), 500);
    } catch (error) {
      console.error('Error delaying reminder:', error);
      toast({
        title: "Hata",
        description: "Hatırlatıcı ertelenirken bir hata oluştu.",
        variant: "destructive",
      });
    }
  };

  const getCustomerDetails = (event: CalendarEvent) => {
    if (event.customer) {
      return event.customer;
    }
    if (event.visit?.customer) {
      return event.visit.customer;
    }
    if ((event.metadata as any)?.customer_name) {
      return {
        first_name: (event.metadata as any).customer_name,
        last_name: null,
        phone: null,
        email: null,
      };
    }
    return null;
  };

  // Filter out completed reminders for the alert
  const activeReminders = reminders.filter(r => !isEventCompleted(r));

  // Sort reminders: satisfaction calls at the bottom, others on top
  const sortedReminders = [...reminders].sort((a, b) => {
    const aIsSatisfaction = isSatisfactionCall(a);
    const bIsSatisfaction = isSatisfactionCall(b);
    
    // If one is satisfaction call and the other isn't, satisfaction goes to bottom
    if (aIsSatisfaction && !bIsSatisfaction) return 1;
    if (!aIsSatisfaction && bIsSatisfaction) return -1;
    
    // Otherwise maintain original order (by time and priority)
    return 0;
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Hatırlatıcılar yükleniyor...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Alert Banner */}
      {activeReminders.length > 0 && (
        <Alert className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
          <Bell className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900 dark:text-amber-100">
            Bugün {activeReminders.length} Hatırlatıcı Var
          </AlertTitle>
          <AlertDescription className="text-amber-800 dark:text-amber-200">
            Bugün için {activeReminders.length} adet tamamlanmamış hatırlatıcı bulunmaktadır. Lütfen kontrol edin.
          </AlertDescription>
        </Alert>
      )}

      {/* Reminders Card */}
      {reminders.length > 0 && (
        <Card>
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Bugünün Hatırlatıcıları ({reminders.length})
                  </div>
                  <ChevronDown className={cn(
                    "w-4 h-4 transition-transform duration-200",
                    isOpen && "rotate-180"
                  )} />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-3">
            {sortedReminders.map((reminder) => {
              const config = eventConfig[reminder.event_type as keyof typeof eventConfig] || eventConfig['task'];
              const Icon = config.icon;
              const isCompleted = isEventCompleted(reminder);
              const customer = getCustomerDetails(reminder);

              return (
                <div
                  key={reminder.id}
                  className={cn(
                    "p-4 rounded-lg border transition-all duration-200",
                    config.color,
                    isCompleted && "opacity-60"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={cn(
                        "p-2 rounded-lg",
                        config.color
                      )}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className={cn(
                            "font-semibold text-sm",
                            isCompleted && "line-through"
                          )}>
                            {reminder.title}
                          </h4>
                          <Badge variant="outline" className={cn("text-xs", config.color)}>
                            {config.label}
                          </Badge>
                        </div>
                        
                        {reminder.start_time && (
                          <p className="text-xs text-muted-foreground mb-1">
                            🕐 {reminder.start_time}
                          </p>
                        )}
                        
                        {customer && (
                          <p className="text-xs text-muted-foreground mb-1">
                            👤 {formatCustomerName(customer)}
                            {customer.phone && ` • ${customer.phone}`}
                          </p>
                        )}
                        
                        {reminder.description && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                            {reminder.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Tamamlandı Button - Available for all reminders */}
                      <Button
                        variant={isCompleted ? "outline" : "default"}
                        size="sm"
                        onClick={() => toggleEventCompletion(reminder)}
                        disabled={isUpdating}
                        className={cn(
                          "gap-1",
                          isCompleted 
                            ? "bg-green-50 hover:bg-green-100 text-green-700 border-green-300" 
                            : "bg-green-600 hover:bg-green-700 text-white"
                        )}
                      >
                        {isCompleted ? (
                          <>
                            <Circle className="w-3 h-3" />
                            <span className="hidden sm:inline">Tekrar Aç</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="hidden sm:inline">Tamamlandı</span>
                          </>
                        )}
                      </Button>
                      
                      {/* Ertelendi Button - Available for all non-completed reminders */}
                      {!isCompleted && (
                        <Popover 
                          open={delayEventId === reminder.id} 
                          onOpenChange={(open) => {
                            if (open) {
                              setDelayEventId(reminder.id);
                              setDelayDate(addDays(new Date(), 1));
                            } else {
                              setDelayEventId(null);
                              setDelayDate(undefined);
                            }
                          }}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1"
                            >
                              <Clock className="w-3 h-3" />
                              <span className="hidden sm:inline">Ertelendi</span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="end">
                            <div className="p-4 space-y-4">
                              <div className="space-y-2">
                                <h4 className="font-medium text-sm">Yeni Tarih Seçin</h4>
                                <Calendar
                                  mode="single"
                                  selected={delayDate}
                                  onSelect={(date) => {
                                    if (date) {
                                      setDelayDate(date);
                                    }
                                  }}
                                  disabled={(date) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return date < today;
                                  }}
                                  initialFocus
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => {
                                    setDelayEventId(null);
                                    setDelayDate(undefined);
                                  }}
                                >
                                  İptal
                                </Button>
                                <Button
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => {
                                    if (delayDate && reminder.id) {
                                      delayReminder(reminder.id, delayDate);
                                    }
                                  }}
                                  disabled={!delayDate}
                                >
                                  Ertele
                                </Button>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => dismissReminder(reminder)}
                        disabled={isUpdating}
                        className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>
      )}
    </>
  );
};

